import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchSchoolYears } from '../slices/schoolYearsSlice';

import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Avatar, 
  Stack, 
  InputAdornment,
  Tabs,
  Tab,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Tooltip
} from '@mui/material';
import { 
  IconUpload, 
  IconSchool, 
  IconMapPin, 
  IconPhone, 
  IconMail,
  IconSettings,
  IconCurrencyDollar as IconCash,
  IconBook,
  IconBus,
  IconShirt,
  IconBuildingStore,
  IconUserPlus,
  IconTrash,
  IconEdit,
  IconCheck,
  IconX,
  IconCalendar,
  IconCalendarEvent,
  IconCalendarStats,
  IconChalkboard as IconTeach,
  IconSteeringWheel
} from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchEmployeeTypes,
  addEmployeeType,
  updateEmployeeType,
  deleteEmployeeType
} from '../slices/employeeTypesSlice';
import { useToast } from '../context/ToastContext';

import {
  fetchAuthorizedPayers,
  addAuthorizedPayer,
  deleteAuthorizedPayer,
  updateAuthorizedPayer
} from '../slices/authorizedPayersSlice'; 
import { fetchSchoolFees, updateSchoolFees } from '../slices/schoolFeesSlice';
import { motion } from 'framer-motion';
import {
  fetchVoucherNumberSettings,
  getCurrentNumbers,
  saveVoucherNumberSettings
} from '../slices/voucherNumberSettingsSlice';

// Place these helpers at the top-level, outside the component
const normalized = (str) =>
  (str || '').replace(/[ة]/g, "").replace(/[\/]/g, "").trim().toLowerCase();

const isFixedType = (name) => {
  const norm = normalized(name);
  return (
    norm === normalized("معلم") ||
    norm === normalized("معلمة") ||
    norm === normalized("معلم/ة") ||
    norm === normalized("سائق") ||
    norm === normalized("سائقة") ||
    norm === normalized("سائق/ة")
  );
};

// 🎨 Design System tokens
const MotionPaper = motion(Paper);
const FONT_FAMILY = 'Cairo, Tajawal, sans-serif';
const DS = {
  primary: '#4338CA',
  primaryHover: '#3730A3',
  primaryLight: '#EEF2FF',
  success: '#16A34A',
  successLight: '#DCFCE7',
  warning: '#D97706',
  warningLight: '#FEF3C7',
  error: '#DC2626',
  errorLight: '#FEE2E2',
  info: '#0284C7',
  infoLight: '#E0F2FE',
  pageBg: '#F8F9FF',
  surface: '#FFFFFF',
  shadow: '0 2px 12px rgba(67,56,202,0.07)',
  text: '#1E1B4B',
  text2: '#6B7280',
  border: '#E5E7EB',
};

// Reusable section-card style
const sectionCardSx = {
  p: { xs: 2.5, md: 4 },
  borderRadius: 3,
  bgcolor: DS.surface,
  boxShadow: DS.shadow,
  border: `1px solid ${DS.border}`,
};

// Icon tile wrapper style
const iconTileSx = {
  width: 40,
  height: 40,
  borderRadius: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: DS.primaryLight,
  color: DS.primary,
  flexShrink: 0,
};

// Primary contained button style
const primaryBtnSx = {
  bgcolor: DS.primary,
  color: '#fff',
  borderRadius: 2.5,
  boxShadow: 'none',
  fontWeight: 700,
  textTransform: 'none',
  fontFamily: FONT_FAMILY,
  '&:hover': { bgcolor: DS.primaryHover, boxShadow: 'none' },
};

// Outlined primary button style
const outlinedBtnSx = {
  borderColor: DS.primary,
  color: DS.primary,
  borderRadius: 2.5,
  fontWeight: 700,
  textTransform: 'none',
  fontFamily: FONT_FAMILY,
  '&:hover': { borderColor: DS.primary, bgcolor: DS.primaryLight },
};

// Voucher Number Settings Tab Component
const VoucherNumberSettingsTab = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { settings, currentNumbers, loading, saving, error } = useSelector(
    state => state.voucherNumberSettings || {}
  );

  const [paymentStartingNumber, setPaymentStartingNumber] = useState(200000000);
  const [recipientStartingNumber, setRecipientStartingNumber] = useState(200000000);

  useEffect(() => {
    dispatch(fetchVoucherNumberSettings());
    dispatch(getCurrentNumbers());
  }, [dispatch]);

  useEffect(() => {
    const paymentSettings = settings.find(s => s.voucher_type === 'payment');
    const recipientSettings = settings.find(s => s.voucher_type === 'recipient');
    
    if (paymentSettings) {
      setPaymentStartingNumber(paymentSettings.starting_number || 200000000);
    }
    if (recipientSettings) {
      setRecipientStartingNumber(recipientSettings.starting_number || 200000000);
    }
  }, [settings]);

  const handleSavePayment = async () => {
    try {
      await dispatch(saveVoucherNumberSettings({
        voucherType: 'payment',
        startingNumber: paymentStartingNumber
      })).unwrap();
      showToast('✅ تم حفظ إعدادات سند الصرف بنجاح');
      dispatch(getCurrentNumbers());
    } catch (error) {
      showToast('❌ فشل في حفظ الإعدادات');
    }
  };

  const handleSaveRecipient = async () => {
    try {
      await dispatch(saveVoucherNumberSettings({
        voucherType: 'recipient',
        startingNumber: recipientStartingNumber
      })).unwrap();
      showToast('✅ تم حفظ إعدادات سند القبض بنجاح');
      dispatch(getCurrentNumbers());
    } catch (error) {
      showToast('❌ فشل في حفظ الإعدادات');
    }
  };

  return (
    // 🎨 Design System Applied
    <MotionPaper
      component={motion.div}
      elevation={0}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
      sx={sectionCardSx}
    >
      <Stack useFlexGap direction="row" alignItems="center" spacing={1.5} mb={3}>
        <Box sx={{ ...iconTileSx, width: 48, height: 48, borderRadius: 2.5 }}>
          <IconCash size={26} />
        </Box>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ color: DS.text, fontFamily: FONT_FAMILY }}
        >
          إعدادات أرقام السندات
        </Typography>
      </Stack>

      <Alert
        severity="info"
        sx={{
          mb: 3,
          borderRadius: 2.5,
          bgcolor: DS.infoLight,
          color: DS.info,
          border: `1px solid ${DS.info}22`,
          fontFamily: FONT_FAMILY,
          '& .MuiAlert-icon': { color: DS.info },
        }}
      >
        <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY }}>
          يمكنك تحديد الرقم الأولي لكل نوع من السندات. سيتم زيادة الرقم تلقائياً عند إنشاء سندات جديدة.
          <br />
          <strong>ملاحظة:</strong> السندات الموجودة لن تتأثر بهذه الإعدادات.
        </Typography>
      </Alert>

      <Stack spacing={3}>
        {/* Payment Settings */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${DS.border}`,
            boxShadow: DS.shadow,
            overflow: 'hidden',
          }}
        >
          <CardHeader
            title="سند الصرف"
            subheader="إعدادات أرقام سندات الصرف"
            avatar={
              <Box sx={{ ...iconTileSx, bgcolor: DS.errorLight, color: DS.error }}>
                <IconCash size={22} />
              </Box>
            }
            titleTypographyProps={{ fontWeight: 700, color: DS.text, fontFamily: FONT_FAMILY }}
            subheaderTypographyProps={{ color: DS.text2, fontFamily: FONT_FAMILY }}
          />
          <CardContent>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="الرقم الأولي"
                type="number"
                value={paymentStartingNumber}
                onChange={(e) => setPaymentStartingNumber(parseInt(e.target.value) || 200000000)}
                helperText="الرقم الذي سيبدأ منه ترقيم سندات الصرف الجديدة"
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
              {currentNumbers?.payment && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: DS.primaryLight,
                    border: `1px solid ${DS.border}`,
                  }}
                >
                  <Typography variant="body2" sx={{ color: DS.text2, fontFamily: FONT_FAMILY }}>
                    الرقم الحالي: <strong style={{ color: DS.primary }}>{currentNumbers.payment.current_number || 'لم يتم التحديد'}</strong>
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSavePayment}
                disabled={saving}
                startIcon={<IconCheck />}
                sx={{ ...primaryBtnSx, alignSelf: 'flex-start', px: 3, py: 1 }}
              >
                {saving ? 'جاري الحفظ...' : 'حفظ إعدادات سند الصرف'}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Recipient Settings */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${DS.border}`,
            boxShadow: DS.shadow,
            overflow: 'hidden',
          }}
        >
          <CardHeader
            title="سند القبض"
            subheader="إعدادات أرقام سندات القبض"
            avatar={
              <Box sx={{ ...iconTileSx, bgcolor: DS.successLight, color: DS.success }}>
                <IconCash size={22} />
              </Box>
            }
            titleTypographyProps={{ fontWeight: 700, color: DS.text, fontFamily: FONT_FAMILY }}
            subheaderTypographyProps={{ color: DS.text2, fontFamily: FONT_FAMILY }}
          />
          <CardContent>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="الرقم الأولي"
                type="number"
                value={recipientStartingNumber}
                onChange={(e) => setRecipientStartingNumber(parseInt(e.target.value) || 200000000)}
                helperText="الرقم الذي سيبدأ منه ترقيم سندات القبض الجديدة"
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
              {currentNumbers?.recipient && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: DS.primaryLight,
                    border: `1px solid ${DS.border}`,
                  }}
                >
                  <Typography variant="body2" sx={{ color: DS.text2, fontFamily: FONT_FAMILY }}>
                    الرقم الحالي: <strong style={{ color: DS.primary }}>{currentNumbers.recipient.current_number || 'لم يتم التحديد'}</strong>
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveRecipient}
                disabled={saving}
                startIcon={<IconCheck />}
                sx={{ ...primaryBtnSx, alignSelf: 'flex-start', px: 3, py: 1 }}
              >
                {saving ? 'جاري الحفظ...' : 'حفظ إعدادات سند القبض'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{
            mt: 3,
            borderRadius: 2.5,
            bgcolor: DS.errorLight,
            color: DS.error,
            fontFamily: FONT_FAMILY,
            '& .MuiAlert-icon': { color: DS.error },
          }}
        >
          {error.message || 'حدث خطأ أثناء حفظ الإعدادات'}
        </Alert>
      )}
    </MotionPaper>
  );
};

const SettingsPage = () => {
  const { showToast } = useToast(); // or your equivalent

  const [activeTab, setActiveTab] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: schoolFeesData, loading: feesLoading, error: feesError } = useSelector((state) => state.schoolFees || {});


  // Fix: Safely access account and its properties to avoid destructuring undefined
  const account = useSelector((state) => state.account?.user?.account) || {};
  // Get the ACTIVE year, not just the first one in the list
  const schoolYearsList = useSelector((state) => state.schoolYears?.list || []);
  const schoolYears = schoolYearsList.find(y => y.is_active) || schoolYearsList[0] || {};
  const start_school_date = schoolYears.start_date || '';
  const PHONE_CODE_OPTIONS = [
  { value: '972', label: '+972', flag: '🇵🇸' },
  { value: '970', label: '+970', flag: '🇵🇸' }
];
  const end_school_date = schoolYears.end_date || '';

  // School Profile Data
  const [schoolData, setSchoolData] = useState({
    logo: '',
    name: '',
    address: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    const stored = localStorage.getItem('schoolInfo');
    if (stored) {
      const school = JSON.parse(stored);
      setSchoolData({
        logo: school.logo || '',
        name: school.name || '',
        address: school.address || '',
        phone: school.phone_number || '',
        email: school.email || ''
      });
    }
  }, []);

  // Fixed Employee Types
  const fixedEmployeeTypes = [
    { id: 'teacher', name: 'معلم/ة', isFixed: true },
    { id: 'driver', name: 'سائق/ة', isFixed: true }
  ];

  // Fee Settings Data
  const [feeSettings, setFeeSettings] = useState({
  scope: 'all',
  selectedClass: '',
  selectedStudent: '',
  fees: {
    tuition: 0,
    books: 0,
    transportation: 0,
    uniform: 0
  }
});

  // Fee Edit State
  const [editingFees, setEditingFees] = useState(false);
  const [tempFeeSettings, setTempFeeSettings] = useState(feeSettings);

  // Approved Payment Destinations
  const { items: paymentDestinations = [], loadingDestinations } = useSelector(state => state.authorizedPayers || {});
const [newDestination, setNewDestination] = useState({ 
  name: '', 
  phone_number: '',
  phone_code: '970' // Add default phone code
});  const [editDestinationId, setEditDestinationId] = useState(null);
  const [editDestinationName, setEditDestinationName] = useState({ name: '', phone_number: '' });
  const [editDestinationData, setEditDestinationData] = useState({ 
  name: '', 
  phone_number: '',
  phone_code: '970' // Add default phone code
});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [destinationToDelete, setDestinationToDelete] = useState(null);

  // Employee Types
  const { items: employeeTypes = [], loadingEmployeeTypes } = useSelector(state => state.employeeTypes || {});
  const [newEmployeeType, setNewEmployeeType] = useState('');
  const [editEmployeeTypeId, setEditEmployeeTypeId] = useState(null);
  const [editEmployeeTypeName, setEditEmployeeTypeName] = useState('');

  // Field Editing States
  const [editingField, setEditingField] = useState(null);
  const [tempFieldValue, setTempFieldValue] = useState('');

  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  // School Profile Handlers
  const handleSchoolChange = (e) => {
    const { name, value } = e.target;
    setSchoolData(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
  if (schoolFeesData) {
    setFeeSettings(prev => ({
      ...prev,
      fees: {
        tuition: schoolFeesData?.school_fee || 0,
        books: schoolFeesData?.books_fee || 0,
        transportation: schoolFeesData?.trans_fee || 0,
        uniform: schoolFeesData?.clothes_fee || 0
      }
    }));
    
    // Also update tempFeeSettings if not currently editing
    if (!editingFees) {
      setTempFeeSettings(prev => ({
        ...prev,
        fees: {
          tuition: schoolFeesData?.school_fee || 0,
          books: schoolFeesData?.books_fee || 0,
          transportation: schoolFeesData?.trans_fee || 0,
          uniform: schoolFeesData?.clothes_fee || 0
        }
      }));
    }
  }
}, [schoolFeesData, editingFees]);


  useEffect(() => {
    try {
      dispatch(fetchEmployeeTypes());
      dispatch(fetchSchoolFees());
      dispatch(fetchSchoolYears());
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [dispatch]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSchoolData(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Field Editing Handlers
  const handleStartEditField = (fieldName, currentValue) => {
    setEditingField(fieldName);
    setTempFieldValue(currentValue);
  };

  const handleFieldValueChange = (e) => {
    setTempFieldValue(e.target.value);
  };

  const handleSaveField = (fieldName) => {
    setSchoolData(prev => ({ ...prev, [fieldName]: tempFieldValue }));
    setEditingField(null);
    setTempFieldValue('');
  };

  const handleCancelEditField = () => {
    setEditingField(null);
    setTempFieldValue('');
  };

  // Fee Settings Handlers
  const handleFeeScopeChange = (e) => {
    const { name, value } = e.target;
    setFeeSettings(prev => ({ 
      ...prev, 
      [name]: value,
      selectedClass: name === 'scope' && value !== 'class' ? '' : prev.selectedClass,
      selectedStudent: name === 'scope' && value !== 'student' ? '' : prev.selectedStudent
    }));
  };

  // Fee Edit Handlers
  const handleFeeChange = (e) => {
    const { name, value } = e.target;
    setTempFeeSettings(prev => ({
      ...prev,
      fees: {
        ...prev.fees,
        [name]: value
      }
    }));
  };

  const handleStartEditFees = () => {
    setTempFeeSettings(feeSettings);
    setEditingFees(true);
  };

  const handleSaveFees = async () => {
  try {
    await dispatch(updateSchoolFees({
      school_fee: tempFeeSettings.fees.tuition,
      books_fee: tempFeeSettings.fees.books,
      trans_fee: tempFeeSettings.fees.transportation,
      clothes_fee: tempFeeSettings.fees.uniform,
      school_year: schoolYears.id
    })).unwrap();
    
    // Update local state after successful API call
    setFeeSettings(tempFeeSettings);
    setEditingFees(false);
    showToast('✅ تم حفظ الرسوم بنجاح');
  } catch (error) {
    console.error('Error saving fees:', error);
    showToast('❌ حدث خطأ أثناء حفظ الرسوم');
  }
};

  const handleCancelEditFees = () => {
    setEditingFees(false);
  };

  // Payment Destinations Handlers
const handleAddDestination = async () => {
  if (newDestination.name?.trim()) {
    try {
      await dispatch(addAuthorizedPayer({
        name: newDestination.name.trim(),
        display_value: newDestination.name.trim(),
        phone_number: newDestination.phone_number?.trim() || null,
        phone_code: newDestination.phone_code || '970'
      })).unwrap();
      
      // Reset the form after successful addition
      setNewDestination({ name: '', phone_number: '' });
      showToast('✅ تم إضافة جهة الصرف بنجاح');
    } catch (error) {
      console.error("Error adding destination:", error);
      showToast('❌ حدث خطأ أثناء إضافة جهة الصرف');
    }
  }
};

const handleStartEditDestination = (id) => {
  const destination = paymentDestinations.find(d => d.id === id);
  if (destination && !destination.isDefault) {
    setEditDestinationId(id);
    setEditDestinationData({
      name: destination.name || destination.display_value || '',
      phone_number: destination.phone_number || '',
      phone_code: destination.phone_code || '972'
    });
  }
};

const handleSaveEditDestination = async () => {
  if (editDestinationData.name?.trim()) {
    try {
      await dispatch(updateAuthorizedPayer({
        id: editDestinationId,
        name: editDestinationData.name.trim(),
        display_value: editDestinationData.name.trim(),
        phone_number: editDestinationData.phone_number?.trim() || null,
        phone_code: editDestinationData.phone_code || '970'
      })).unwrap();
      
      setEditDestinationId(null);
      setEditDestinationData({ name: '', phone_number: '' });
      showToast('✅ تم تحديث جهة الصرف بنجاح');
    } catch (error) {
      console.error("Error updating destination:", error);
      showToast('❌ حدث خطأ أثناء تحديث جهة الصرف');
    }
  }
};

const handleCancelEditDestination = () => {
  setEditDestinationId(null);
  setEditDestinationData({ name: '', phone_number: '', phone_code: '970' });
};

const handleOpenDeleteDialog = (id) => {
  const destination = paymentDestinations.find(d => d.id === id);
  if (destination && !destination.isDefault) {
    setDestinationToDelete(id);
    setDeleteDialogOpen(true);
  }
};

const handleConfirmDeleteDestination = async () => {
  try {
    await dispatch(deleteAuthorizedPayer(destinationToDelete)).unwrap();
    setDeleteDialogOpen(false);
    setDestinationToDelete(null);
    showToast('✅ تم حذف جهة الصرف بنجاح');
  } catch (error) {
    console.error("Error deleting destination:", error);
    showToast('❌ حدث خطأ أثناء حذف جهة الصرف');
  }
};

const handleCancelDeleteDestination = () => {
  setDeleteDialogOpen(false);
  setDestinationToDelete(null);
};

  // Employee Types Handlers
  const handleAddEmployeeType = () => {
    const normalizedNew = normalized(newEmployeeType);

    const exists = (employeeTypes || []).some((type) => normalized(type.name) === normalizedNew);

    if (exists) {
      alert("لا يمكن إضافة هذا النوع لأنه موجود بالفعل");
      return;
    }

    try {
      dispatch(addEmployeeType({
        name: newEmployeeType.trim(),
        display_value: newEmployeeType.trim()
      }));
      setNewEmployeeType('');
    } catch (error) {
      console.error("Error adding employee type:", error);
      alert('حدث خطأ أثناء إضافة نوع الموظف');
    }
  };

  const handleStartEditEmployeeType = (id) => {
    // Prevent editing fixed types
    if (id === 'teacher' || id === 'driver') return;
    const employeeType = employeeTypes.find(e => e.id === id);
    if (employeeType) {
      setEditEmployeeTypeId(id);
      setEditEmployeeTypeName(employeeType.name);
    }
  };

  const handleSaveEditEmployeeType = () => {
    // Prevent renaming to a fixed type
    const forbidden = ['teacher', 'driver', 'معلم/ة', 'سائق/ة'];
    if (
      forbidden.includes(editEmployeeTypeName.trim()) ||
      forbidden.includes(editEmployeeTypeName.trim().toLowerCase())
    ) {
      alert('لا يمكن تغيير الاسم إلى نوع موظف ثابت (معلم/ة أو سائق/ة)');
      return;
    }
    if (editEmployeeTypeName.trim()) {
      try {
        dispatch(updateEmployeeType({
          id: editEmployeeTypeId,
          name: editEmployeeTypeName.trim(),
          display_value: editEmployeeTypeName.trim()
        }));
        setEditEmployeeTypeId(null);
        setEditEmployeeTypeName('');
      } catch (error) {
        console.error("Error updating employee type:", error);
        alert('حدث خطأ أثناء تحديث نوع الموظف');
      }
    }
  };

  const handleDeleteEmployeeType = (id) => {
    // Prevent deleting fixed types
    if (id === 'teacher' || id === 'driver') return;
    try {
      dispatch(deleteEmployeeType(id));
    } catch (error) {
      console.error("Error deleting employee type:", error);
      alert('حدث خطأ أثناء حذف نوع الموظف');
    }
  };

  // Add this function to fix the error
  const handleCancelEditEmployeeType = () => {
    setEditEmployeeTypeId(null);
    setEditEmployeeTypeName('');
  };

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: FONT_FAMILY,
        p: { xs: 2, md: 3 },
        bgcolor: DS.pageBg,
        minHeight: '100vh',
      }}
    >
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header Section */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2.5,
              width: 52,
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconSettings size={30} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              color="#fff"
              sx={{ fontFamily: FONT_FAMILY }}
            >
              الإعدادات
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                mt: 0.25,
                fontFamily: FONT_FAMILY,
              }}
            >
              إدارة ملف المدرسة وأنواع الموظفين وأرقام السندات
            </Typography>
          </Box>
        </Stack>
      </MotionPaper>

      <Tabs
        value={activeTab}
        onChange={handleChangeTab}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          bgcolor: DS.surface,
          borderRadius: 3,
          border: `1px solid ${DS.border}`,
          boxShadow: DS.shadow,
          px: 1,
          minHeight: 56,
          '& .MuiTabs-indicator': { backgroundColor: DS.primary, height: 3, borderRadius: 3 },
          '& .MuiTab-root': {
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            textTransform: 'none',
            color: DS.text2,
            minHeight: 56,
          },
          '& .Mui-selected': { color: `${DS.primary} !important` },
        }}
      >
        <Tab
          icon={<IconSchool size={22} />}
          iconPosition="start"
          label="ملف المدرسة"
        />
        <Tab
          icon={<IconUserPlus size={22} />}
          iconPosition="start"
          label="أنواع الموظفين"
        />
        <Tab
          icon={<IconCash size={22} />}
          iconPosition="start"
          label="إعدادات أرقام السندات"
        />
      </Tabs>

      {activeTab === 0 && (
        <>
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            sx={{ ...sectionCardSx, maxWidth: 800, mx: 'auto' }}
          >
            <Stack spacing={4}>
              {/* Logo Upload */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar
                src={schoolData.logo}
                sx={{
                  width: 120,
                  height: 120,
                  background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
                  boxShadow: DS.shadow,
                }}
              >
                <IconSchool size={60} />
              </Avatar>
              <Box>
                {/* Remove upload button */}
                {/* <Button ...>رفع لوجو المدرسة</Button> */}
                <Typography variant="caption" sx={{ color: DS.text2, fontFamily: FONT_FAMILY }}>
                  PNG أو JPG (الحد الأقصى 800x400 بكسل)
                </Typography>
              </Box>
            </Box>

            {/* School Information Fields */}
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                label="اسم المدرسة"
                value={schoolData.name}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconSchool />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                label="عنوان المدرسة"
                value={schoolData.address}
                multiline
                rows={2}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconMapPin />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                label="رقم التلفون"
                value={schoolData.phone}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconPhone />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                label="ايميل المدرسة"
                value={schoolData.email}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconMail />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Academic Year Section */}
            <Card
              variant="outlined"
              sx={{
                mt: 2,
                borderRadius: 3,
                bgcolor: DS.primaryLight,
                border: `1px solid ${DS.border}`,
              }}
            >
              <CardHeader
                title={
                  <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={iconTileSx}>
                      <IconCalendarStats size={22} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: DS.text, fontFamily: FONT_FAMILY }}>
                      السنة الدراسية
                    </Typography>
                  </Stack>
                }
              />
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 250, color: DS.text, fontFamily: FONT_FAMILY }}>
                      <IconCalendarEvent color={DS.primary} />
                      <strong>السنة الدراسية:</strong> {schoolYears.label || 'لم يتم التحديد بعد'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: DS.text2, fontFamily: FONT_FAMILY }}>
                    الانتقال إلى سنة دراسية جديدة يتم فقط من صفحة "العناصر المفتوحة للسنة الدراسية"، حتى يتم التحقق
                    من الطلاب والشيكات والمدفوعات وترحيل الديون بشكل صحيح قبل إغلاق السنة الحالية.
                  </Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => navigate('/admin/year-transition')}
                      startIcon={<IconCalendarStats />}
                      sx={{ ...outlinedBtnSx, px: 3 }}
                    >
                      الانتقال للسنة الدراسية الجديدة
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>


            {/* Remove "حفظ التغييرات" button */}
          </Stack>
        </MotionPaper>
        </>
      )}

      {activeTab === 1 && (
  <MotionPaper
    component={motion.div}
    elevation={0}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
    sx={{ ...sectionCardSx, maxWidth: 800, mx: 'auto' }}
  >
    <Stack spacing={3}>
      <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
        <Box sx={iconTileSx}>
          <IconUserPlus size={22} />
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ color: DS.text, fontFamily: FONT_FAMILY }}>
          أنواع الموظفين
        </Typography>
      </Stack>

      <Divider sx={{ borderColor: DS.border }} />

      <Box>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: DS.text2, fontFamily: FONT_FAMILY }}>
          أنواع مخصصة
        </Typography>

        {/* Add new employee type */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            label="إضافة نوع موظف جديد"
            value={newEmployeeType}
            onChange={(e) => setNewEmployeeType(e.target.value)}
            placeholder="اسم النوع"
          />
          <Button
            variant="contained"
            onClick={handleAddEmployeeType}
            disabled={!newEmployeeType.trim()}
            startIcon={<IconUserPlus size={18} />}
            sx={{ ...primaryBtnSx, px: 3, whiteSpace: 'nowrap' }}
          >
            إضافة
          </Button>
        </Box>

        {/* Employee types list */}
        <TableContainer sx={{ borderRadius: 2, border: `1px solid ${DS.border}`, overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: DS.primaryLight }}>
                <TableCell sx={{ fontWeight: 700, color: DS.text, fontFamily: FONT_FAMILY, borderColor: DS.border }}>النوع</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: DS.text, fontFamily: FONT_FAMILY, borderColor: DS.border }}>الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingEmployeeTypes ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">جاري التحميل...</TableCell>
                </TableRow>
              ) : employeeTypes && employeeTypes.length > 0 ? (
                employeeTypes.map((type) => {
                  const isFixed = isFixedType(type.name);

                  return (
                    <TableRow key={type.id}>
                      <TableCell>
                        {editEmployeeTypeId === type.id && !isFixed ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              fullWidth
                              value={editEmployeeTypeName}
                              onChange={(e) => setEditEmployeeTypeName(e.target.value)}
                              size="small"
                            />
                            <IconButton sx={{ color: DS.success }} onClick={handleSaveEditEmployeeType}>
                              <IconCheck size={20} />
                            </IconButton>
                            <IconButton sx={{ color: DS.error }} onClick={handleCancelEditEmployeeType}>
                              <IconX size={20} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: DS.text, fontFamily: FONT_FAMILY }}>
                            {(type.id === 'teacher' || normalized(type.name) === normalized("معلم/ة")) && (
                              <IconTeach size={20} color={DS.primary} />
                            )}
                            {(type.id === 'driver' || normalized(type.name) === normalized("سائق/ة")) && (
                              <IconSteeringWheel size={20} color={DS.primary} />
                            )}
                            {type.name}
                            {isFixed && (
                              <Chip
                                label="نوع ثابت"
                                size="small"
                                sx={{
                                  ml: 1,
                                  bgcolor: DS.primaryLight,
                                  color: DS.primary,
                                  fontWeight: 700,
                                  fontFamily: FONT_FAMILY,
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {!isFixed && (
                          <>
                            <IconButton
                              sx={{ color: DS.primary }}
                              onClick={() => handleStartEditEmployeeType(type.id)}
                              disabled={editEmployeeTypeId !== null}
                            >
                              <IconEdit size={20} />
                            </IconButton>
                            <IconButton
                              sx={{ color: DS.error }}
                              onClick={() => handleDeleteEmployeeType(type.id)}
                              disabled={editEmployeeTypeId !== null}
                            >
                              <IconTrash size={20} />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ color: DS.text2, fontFamily: FONT_FAMILY, borderColor: DS.border }}>لا توجد أنواع موظفين مخصصة</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  </MotionPaper>
)}

      {activeTab === 2 && (
        <VoucherNumberSettingsTab />
      )}

    </Box>
    </Box>
  );
};

export default SettingsPage;